import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../config/api';
import {
  resolveWidgetDataPath,
  pickWidgetSaveDataPath,
  formatWidgetDisplayValue,
  resolveWidgetConfig,
} from '../../utils/signalKValueFormat';
import { useSignalkDashboardData } from '../../hooks/useSignalkDashboardData';
import DataSourceIndicator from './widgets/DataSourceIndicator';
import { INSTRUMENT } from './widgets/instrumentLook';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  PlusIcon,
  Cog6ToothIcon,
  TrashIcon,
  Squares2X2Icon,
  RectangleGroupIcon,
  EyeIcon,
  PencilIcon,
  ArrowsPointingOutIcon,
  Bars3Icon,
  XMarkIcon,
  CheckIcon,
  EllipsisVerticalIcon
} from '@heroicons/react/24/outline';
import WidgetEditor from './WidgetEditor';
import { resumeWidgetEditorFromPicker } from '../../utils/signalkPickerReturn';
import {
  normalizeSection,
  normalizeSections,
  sectionDisplayName,
  sectionColumns,
  sectionApiPayload,
} from '../../utils/dashboardSectionUtils';
import {
  normalizeWidget,
  normalizeWidgets,
  widgetDisplayName,
  widgetApiPayload,
  dashboardLayoutPayload,
} from '../../utils/dashboardWidgetUtils';
import {
  sectionGridColumnCount,
  widgetGridColumnSpan,
} from '../../utils/dashboardGridUtils';
import { WIDGET_TYPES } from './widgets/UnifiedWidgetSystem';
import BilgeMonitorSettingsModal from './bilge/BilgeMonitorSettingsModal';
import BilgeMonitorSection from './bilge/BilgeMonitorSection';
import { BILGE_LAYOUT_SORT_ID, buildLayoutSortIds, clampBilgeInsertIndex } from '../../utils/bilgeDashboardLayout';

const BuilderContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  display: flex;
  flex-direction: column;
  max-width: 1024px;
  margin: 0 auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary && `
    background: ${theme.colors.primary.main};
    color: white;
  
  &:hover {
    background: ${theme.colors.primary.dark};
  }
  `}
  
  ${props => props.secondary && `
  background: ${theme.colors.background.secondary};
    color: ${theme.colors.text.primary};
    border: 1px solid ${theme.colors.border.primary};
    
    &:hover {
      background: ${theme.colors.background.tertiary};
    }
  `}
  
  ${props => props.danger && `
    background: ${theme.colors.error.main};
    color: white;
    
    &:hover {
      background: ${theme.colors.error.dark};
    }
  `}
`;

const DashboardArea = styled.div`
  flex: 1;
  position: relative;
`;

const AddSectionButton = styled.div`
  width: 100%;
  min-height: 120px;
  border: 2px dashed ${theme.colors.border.secondary};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${theme.colors.background.secondary};
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    background: ${theme.colors.primary.light}10;
  }
`;

const AddSectionText = styled.div`
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

const EditorHint = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.secondary};
  margin: 0 0 18px 0;
  line-height: 1.5;
`;

const BilgeEditShell = styled.div`
  position: relative;
  margin-bottom: 24px;
  border: 2px solid ${theme.colors.primary.main};
  border-radius: 12px;
  overflow: hidden;
  background: ${theme.colors.background.primary};
`;

const BilgeEditToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: ${theme.colors.background.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const BilgeConfigureBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    background: ${theme.colors.background.tertiary};
  }
`;

// Section Components
const SectionContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  
  &:hover .section-hover-overlay {
    opacity: 1;
    pointer-events: auto;
  }
`;

const SectionHoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px 12px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s ease;
  backdrop-filter: blur(2px);
  z-index: 10;
`;

const SectionControls = styled.div`
  display: flex;
  gap: 8px;
`;

const SectionControlButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #475569;
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 700;
  color: ${INSTRUMENT.accent};
  margin: 0;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const SectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #94a3b8;
  font-size: 14px;
`;

const WidgetsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns || 2}, minmax(0, 1fr));
  gap: 16px;
  padding: 8px 0;
  width: 100%;
`;

const WidgetContainer = styled.div`
  position: relative;
  background: ${INSTRUMENT.cardBg};
  border: 1px solid ${INSTRUMENT.cardBorder};
  border-radius: 12px;
  padding: 16px;
  min-height: 120px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
  grid-column: span ${props => props.$span || 1};
  
  &:hover .widget-hover-overlay {
    opacity: 1;
    pointer-events: auto;
  }
`;

const WidgetHoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.82);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  transition: all 0.2s ease;
  backdrop-filter: blur(2px);
`;

const WidgetControls = styled.div`
  display: flex;
  gap: 8px;
`;

const WidgetControlButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const WidgetTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${INSTRUMENT.label};
  margin: 0;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  flex: 1;
  min-width: 0;
`;

const WidgetTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 12px;
`;

const WidgetContent = styled.div`
  font-size: 1.65rem;
  font-weight: 700;
  color: ${INSTRUMENT.value};
  text-align: left;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  letter-spacing: -0.02em;
`;

const AddWidgetButton = styled.div`
  width: 100%;
  min-height: 100px;
  border: 2px dashed ${INSTRUMENT.cardHoverBorder};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${INSTRUMENT.cardBg};
  position: relative;
  z-index: 20;
  
  &:hover {
    border-color: ${INSTRUMENT.accent};
    background: #111827;
  }
`;

const AddWidgetText = styled.div`
  color: ${INSTRUMENT.label};
  font-size: 14px;
  font-weight: 500;
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #1e293b;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

// Sortable Components
/** Non-draggable row so @dnd-kit SortableContext item count matches useSortable children (bilge between sections). */
const SortableBilgeRail = ({ children }) => {
  const { attributes, setNodeRef, transform, transition } = useSortable({
    id: BILGE_LAYOUT_SORT_ID,
    disabled: true,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children}
    </div>
  );
};

const SortableWidget = ({ widget, sectionId, sectionColumns, onEditWidget, onDeleteWidget, getWidgetSignalData }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: widget.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <WidgetContainer ref={setNodeRef} style={style} {...attributes} $span={widgetGridColumnSpan(widget, sectionColumns)}>
      <WidgetHoverOverlay className="widget-hover-overlay">
        <WidgetControls>
          <div {...listeners} style={{ cursor: 'grab', padding: '4px', display: 'flex', alignItems: 'center' }}>
            <Bars3Icon className="w-4 h-4 text-gray-400" />
          </div>
          <WidgetControlButton onClick={() => onEditWidget(widget, sectionId)}>
            <PencilIcon className="w-4 h-4" />
            Edit
          </WidgetControlButton>
          <WidgetControlButton onClick={() => onDeleteWidget(widget.id)}>
            <TrashIcon className="w-4 h-4" />
            Delete
          </WidgetControlButton>
        </WidgetControls>
      </WidgetHoverOverlay>

      <WidgetTitleRow>
        <WidgetTitle>{widgetDisplayName(widget)}</WidgetTitle>
        {(() => {
          const dataPath = resolveWidgetDataPath(widget);
          const widgetConfig = resolveWidgetConfig(widget);
          const signalData = getWidgetSignalData(dataPath, widgetConfig);
          return (
            <DataSourceIndicator sourceType={signalData?.sourceType} lastUpdate={signalData?.lastUpdate} />
          );
        })()}
      </WidgetTitleRow>
      <WidgetContent>
        {(() => {
          const dataPath = resolveWidgetDataPath(widget);
          const widgetConfig = resolveWidgetConfig(widget);
          const signalData = getWidgetSignalData(dataPath, widgetConfig);
          const displayValue = signalData?.value;
          const unit = signalData?.unit || widgetConfig.unit || '';

          let text = '--';
          if (displayValue !== null && displayValue !== undefined && displayValue !== '--') {
            text = formatWidgetDisplayValue(displayValue, widgetConfig, 1);
            if (unit && text !== '--') {
              const u = /^current$/i.test(String(unit).trim()) ? 'A' : unit;
              text = `${text} ${u}`;
            }
          }

          return text;
        })()}
      </WidgetContent>
    </WidgetContainer>
  );
};

const SortableSection = ({ section, onEditSection, onDeleteSection, onAddWidget, onEditWidget, onDeleteWidget, getWidgetSignalData }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SectionContainer
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <SectionHoverOverlay className="section-hover-overlay">
        <SectionControls>
          <SectionControlButton onClick={() => onEditSection(section)}>
            <PencilIcon className="w-4 h-4" />
            Edit
          </SectionControlButton>
          <SectionControlButton onClick={() => onDeleteSection(section.id)}>
            <TrashIcon className="w-4 h-4" />
            Delete
          </SectionControlButton>
        </SectionControls>
      </SectionHoverOverlay>

      <SectionHeader>
        <div>
          <SectionTitle>{sectionDisplayName(section)}</SectionTitle>
          <SectionInfo>
            <span>Columns: {sectionColumns(section)}</span>
            <span>•</span>
            <span>{section.widgets?.length || 0} widgets</span>
          </SectionInfo>
        </div>
        <Bars3Icon className="w-5 h-5 text-gray-400 cursor-move" {...listeners} />
      </SectionHeader>

      <WidgetsGrid $columns={sectionGridColumnCount(section)}>
        <SortableContext items={section.widgets?.map(w => w.id) || []} strategy={verticalListSortingStrategy}>
          {section.widgets?.map((widget) => (
            <SortableWidget
              key={widget.id}
              widget={widget}
              sectionId={section.id}
              sectionColumns={sectionGridColumnCount(section)}
              onEditWidget={onEditWidget}
              onDeleteWidget={onDeleteWidget}
              getWidgetSignalData={getWidgetSignalData}
            />
          ))}
        </SortableContext>
        
        <AddWidgetButton onClick={() => onAddWidget(section.id)}>
          <PlusIcon className="w-6 h-6 text-gray-400" />
          <AddWidgetText>Add Widget</AddWidgetText>
        </AddWidgetButton>
      </WidgetsGrid>
    </SectionContainer>
  );
};

const EditDashboards = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [editingWidget, setEditingWidget] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showBilgeSettings, setShowBilgeSettings] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const { resolveWidgetValue } = useSignalkDashboardData(dashboard?.vessel_id, {
    enableWebSocket: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadDashboard();
  }, [id]);

  useEffect(() => {
    if (!id || !sections.length) return;
    const resumed = resumeWidgetEditorFromPicker(id, sections);
    if (!resumed) return;
    if (resumed.error) {
      toast.error(resumed.error);
      return;
    }
    setEditingWidget(resumed);
    toast.success('Path applied — review settings and save the widget');
  }, [id, sections]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/dashboards/${id}`);
      const dashboardData = response.data;

      if (dashboardData.can_edit === false) {
        toast.error('You have view-only access to this dashboard');
        navigate(`/dashboard/${id}`);
        return;
      }
      
      setDashboard(dashboardData);
      setSections(
        normalizeSections(dashboardData.sections || []).map((section) => ({
          ...section,
          widgets: normalizeWidgets(section.widgets),
        }))
      );
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const parseTheme = (d) => {
    let tc = d?.theme_config;
    if (!tc) return {};
    if (typeof tc === 'string') {
      try {
        tc = JSON.parse(tc);
      } catch {
        return {};
      }
    }
    return tc;
  };

  const getWidgetSignalData = (dataPath, widgetConfig = {}) =>
    resolveWidgetValue(dataPath, widgetConfig);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || String(active.id) === String(over.id)) {
      setActiveId(null);
      return;
    }

    const idEq = (a, b) => String(a) === String(b);

    const isWidgetDrag = sections.some((s) => s.widgets?.some((w) => idEq(w.id, active.id)));
    if (isWidgetDrag) {
      setSections((currentSections) => {
        let sourceSectionIndex = -1;
        let activeWidgetIndex = -1;

        currentSections.forEach((section, sIdx) => {
          const wIdx = section.widgets?.findIndex((w) => idEq(w.id, active.id));
          if (wIdx !== undefined && wIdx !== -1) {
            sourceSectionIndex = sIdx;
            activeWidgetIndex = wIdx;
          }
        });

        let targetSectionIndex = -1;
        let overWidgetIndex = -1;

        currentSections.forEach((section, sIdx) => {
          const wIdx = section.widgets?.findIndex((w) => idEq(w.id, over.id));
          if (wIdx !== undefined && wIdx !== -1) {
            targetSectionIndex = sIdx;
            overWidgetIndex = wIdx;
          }
        });

        if (sourceSectionIndex === -1 || targetSectionIndex === -1) {
          return currentSections;
        }

        if (sourceSectionIndex === targetSectionIndex) {
          const newSections = [...currentSections];
          const widgets = [...newSections[sourceSectionIndex].widgets];
          const reorderedWidgets = arrayMove(widgets, activeWidgetIndex, overWidgetIndex);
          newSections[sourceSectionIndex] = {
            ...newSections[sourceSectionIndex],
            widgets: reorderedWidgets,
          };
          return newSections;
        }

        const newSections = [...currentSections];
        const sourceWidgets = [...newSections[sourceSectionIndex].widgets];
        const targetWidgets = [...newSections[targetSectionIndex].widgets];

        const [movedWidget] = sourceWidgets.splice(activeWidgetIndex, 1);
        movedWidget.sectionId = newSections[targetSectionIndex].id;
        targetWidgets.splice(overWidgetIndex, 0, movedWidget);

        newSections[sourceSectionIndex] = {
          ...newSections[sourceSectionIndex],
          widgets: sourceWidgets,
        };
        newSections[targetSectionIndex] = {
          ...newSections[targetSectionIndex],
          widgets: targetWidgets,
        };

        return newSections;
      });
      setActiveId(null);
      return;
    }

    const isSectionDrag = sections.some((s) => idEq(s.id, active.id));
    if (isSectionDrag) {
      const bilgeIdx = clampBilgeInsertIndex(parseTheme(dashboard)?.bilgeMonitor?.insertIndex, sections.length);
      const layoutIds = buildLayoutSortIds(sections, bilgeIdx);
      const oldIndex = layoutIds.findIndex((lid) => idEq(lid, active.id));
      const newIndex = layoutIds.findIndex((lid) => idEq(lid, over.id));
      if (oldIndex < 0 || newIndex < 0) {
        setActiveId(null);
        return;
      }
      const newLayoutIds = arrayMove(layoutIds, oldIndex, newIndex);
      const newBilgeIdx = newLayoutIds.indexOf(BILGE_LAYOUT_SORT_ID);
      const newSectionIdsOrdered = newLayoutIds.filter((lid) => lid !== BILGE_LAYOUT_SORT_ID);
      const newSections = newSectionIdsOrdered
        .map((sid) => sections.find((s) => idEq(s.id, sid)))
        .filter(Boolean);
      setSections(newSections);

      if (newBilgeIdx !== bilgeIdx) {
        const tc = parseTheme(dashboard);
        const bm = { ...(tc.bilgeMonitor || {}), insertIndex: newBilgeIdx };
        setDashboard((d) => {
          const t = parseTheme(d);
          return { ...d, theme_config: { ...t, bilgeMonitor: bm } };
        });
        try {
          await api.patch(`/api/dashboards/${id}/theme-config`, { theme_config: { bilgeMonitor: bm } });
        } catch (err) {
          console.error('Failed to persist bilge placement after reorder:', err);
          toast.error('Could not save bilge position. Use Configure to set placement.');
        }
      }
      setActiveId(null);
      return;
    }

    setActiveId(null);
  };

  const handleSaveDashboard = async () => {
    try {
      const dashboardData = dashboardLayoutPayload(sections, dashboard.name);

      const response = await api.put(`/api/dashboards/${id}`, dashboardData);
      setDashboard(response.data);
      setSections(
        normalizeSections(response.data.sections || []).map((section) => ({
          ...section,
          widgets: normalizeWidgets(section.widgets),
        }))
      );
      toast.success('Dashboard saved successfully');
    } catch (error) {
      console.error('Error saving dashboard:', error);
      toast.error('Failed to save dashboard');
    }
  };

  const handleAddSection = async (sectionData) => {
    try {
      const response = await api.post(`/api/dashboards/${id}/sections`, sectionData);
      const newSection = normalizeSection(response.data, []);
      setSections(prev => [...prev, newSection]);
      setShowAddSection(false);
      toast.success('Section added successfully');
    } catch (error) {
      console.error('Error adding section:', error);
      toast.error('Failed to add section');
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
  };

  const handleUpdateSection = async (sectionData) => {
    try {
      const response = await api.put(
        `/api/dashboards/${id}/sections/${editingSection.id}`,
        sectionApiPayload(sectionData)
      );
      const updatedSection = response.data;
      setSections(prev => prev.map(s =>
        s.id === updatedSection.id
          ? normalizeSection(updatedSection, s.widgets)
          : s
      ));
      setEditingSection(null);
      toast.success('Section updated successfully');
    } catch (error) {
      console.error('Error updating section:', error);
      toast.error('Failed to update section');
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section? This will also delete all widgets in the section.')) {
      return;
    }

    try {
      await api.delete(`/api/dashboards/${id}/sections/${sectionId}`);
      setSections(prev => prev.filter(s => s.id !== sectionId));
      toast.success('Section deleted successfully');
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to delete section');
    }
  };

  const handleAddWidget = (sectionId) => {
    // Ensure new widgets start as 'digital' by default
    setEditingWidget({ sectionId, type: 'digital' });
  };

  const handleEditWidget = (widget, sectionId) => {
    setEditingWidget({ ...widget, sectionId });
  };

  const handleSaveWidget = async (widgetData) => {
    try {
      const dataPathValue = pickWidgetSaveDataPath(widgetData);
      if (!dataPathValue) {
        toast.error('Select a SignalK data path before saving');
        return;
      }
      const payload = widgetApiPayload({
        ...widgetData,
        data_path: dataPathValue,
        dataPath: dataPathValue,
        data_source: 'signalk',
        config: {
          ...(widgetData.config || {}),
          dataPath: dataPathValue,
        },
      });

      if (editingWidget.id) {
        const response = await api.put(`/api/dashboards/${id}/widgets/${editingWidget.id}`, payload);
        const updatedWidget = normalizeWidget(response.data);

        setSections(prev => prev.map(section => ({
          ...section,
          widgets: section.widgets?.map(w =>
            w.id === updatedWidget.id ? updatedWidget : w
          ) || [],
        })));
      } else {
        const response = await api.post(`/api/dashboards/${id}/widgets`, {
          ...payload,
          section_id: widgetData.sectionId || editingWidget.sectionId,
        });
        const newWidget = normalizeWidget(response.data);

        setSections(prev => prev.map(section =>
          section.id === (widgetData.sectionId || editingWidget.sectionId)
            ? { ...section, widgets: [...(section.widgets || []), newWidget] }
            : section
        ));
      }
      
      setEditingWidget(null);
      toast.success('Widget saved successfully');
    } catch (error) {
      console.error('Error saving widget:', error);
      const msg = error.response?.data?.message || error.response?.data?.error;
      toast.error(msg || 'Failed to save widget');
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    if (!window.confirm('Are you sure you want to delete this widget?')) {
      return;
    }

    try {
      await api.delete(`/api/dashboards/${id}/widgets/${widgetId}`);
      setSections(prev => prev.map(section => ({
        ...section,
        widgets: section.widgets?.filter(w => w.id !== widgetId) || []
      })));
      toast.success('Widget deleted successfully');
    } catch (error) {
      console.error('Error deleting widget:', error);
      toast.error('Failed to delete widget');
    }
  };

  const handleViewDashboard = () => {
    navigate(`/dashboard/${id}`);
  };

  const handleCheckAlarms = async () => {
    try {
      await api.post(`/api/dashboards/${id}/check-alarms`);
      toast.success('Alarm check completed - check console for details');
    } catch (error) {
      console.error('Error checking alarms:', error);
      toast.error('Failed to check alarms');
    }
  };

  const handleSaveBilgeTheme = async (theme_config) => {
    try {
      await api.patch(`/api/dashboards/${id}/theme-config`, { theme_config });
      toast.success('Bilge monitor settings saved');
      setShowBilgeSettings(false);
      await loadDashboard();
    } catch (error) {
      console.error('Error saving bilge settings:', error);
      toast.error('Failed to save bilge settings');
    }
  };

  if (loading) {
    return (
      <BuilderContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>Loading dashboard...</div>
        </div>
      </BuilderContainer>
    );
  }

  if (!dashboard) {
  return (
    <BuilderContainer>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div>Dashboard not found</div>
              </div>
      </BuilderContainer>
    );
  }

  const bilgeThemeForLayout = parseTheme(dashboard).bilgeMonitor;
  const layoutSortIds = buildLayoutSortIds(sections, bilgeThemeForLayout?.insertIndex);
  const showBilgeLiveLayout = !!(bilgeThemeForLayout?.enabled && bilgeThemeForLayout?.pumps?.length);

  return (
    <BuilderContainer>
      <Header>
        <Title>{dashboard.name}</Title>
            <HeaderActions>
          <Button secondary onClick={handleViewDashboard}>
                <EyeIcon className="w-4 h-4" />
            View
              </Button>
          <Button secondary onClick={handleCheckAlarms}>
            <Cog6ToothIcon className="w-4 h-4" />
            Check Alarms
              </Button>
          <Button primary onClick={handleSaveDashboard}>
            <CheckIcon className="w-4 h-4" />
            Save Dashboard
              </Button>
            </HeaderActions>
      </Header>

      <DashboardArea>
        <EditorHint>
          <strong>Sections:</strong> drag the grip (⋮⋮) next to a section title to reorder.{' '}
          <strong>Save each widget</strong> from the widget editor (name, unit, path).{' '}
          <strong>Save Dashboard</strong> only applies section/widget layout order.
        </EditorHint>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={layoutSortIds} strategy={verticalListSortingStrategy}>
            {layoutSortIds.map((lid) => {
              if (lid === BILGE_LAYOUT_SORT_ID) {
                return (
                  <SortableBilgeRail key={BILGE_LAYOUT_SORT_ID}>
                    {showBilgeLiveLayout ? (
                      <BilgeEditShell>
                        <BilgeEditToolbar>
                          <span style={{ fontWeight: 600, color: theme.colors.text.primary }}>Bilge monitor</span>
                          <BilgeConfigureBtn type="button" onClick={() => setShowBilgeSettings(true)}>
                            <Cog6ToothIcon className="w-4 h-4" />
                            Configure
                          </BilgeConfigureBtn>
                        </BilgeEditToolbar>
                        <BilgeMonitorSection dashboardId={Number(id)} config={bilgeThemeForLayout} />
                      </BilgeEditShell>
                    ) : (
                      <BilgeEditShell
                        style={{ borderStyle: 'dashed', borderColor: theme.colors.border.secondary }}
                      >
                        <BilgeEditToolbar>
                          <span style={{ fontWeight: 600, color: theme.colors.text.primary }}>Bilge monitor</span>
                          <BilgeConfigureBtn type="button" onClick={() => setShowBilgeSettings(true)}>
                            <Cog6ToothIcon className="w-4 h-4" />
                            Configure
                          </BilgeConfigureBtn>
                        </BilgeEditToolbar>
                        <div
                          style={{
                            padding: '18px 16px',
                            color: theme.colors.text.secondary,
                            fontSize: 14,
                            lineHeight: 1.45,
                          }}
                        >
                          Enable bilge monitoring and map pump paths here. Placement on the live dashboard matches this
                          order, or set it explicitly under Configure → placement.
                        </div>
                      </BilgeEditShell>
                    )}
                  </SortableBilgeRail>
                );
              }
              const section = sections.find((s) => String(s.id) === String(lid));
              if (!section) return null;
              return (
                <SortableSection
                  key={section.id}
                  section={section}
                  onEditSection={handleEditSection}
                  onDeleteSection={handleDeleteSection}
                  onAddWidget={handleAddWidget}
                  onEditWidget={handleEditWidget}
                  onDeleteWidget={handleDeleteWidget}
                  getWidgetSignalData={getWidgetSignalData}
                />
              );
            })}
          </SortableContext>

          <DragOverlay>
            {activeId ? (
              <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)' }}>
                Dragging section...
                    </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        <AddSectionButton onClick={() => setShowAddSection(true)}>
          <PlusIcon className="w-8 h-8 text-gray-400" />
          <AddSectionText>Add New Section</AddSectionText>
        </AddSectionButton>
      </DashboardArea>

      {/* Section Editor Modal */}
      {editingSection && (
        <ModalOverlay onClick={() => setEditingSection(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Section</ModalTitle>
              <CloseButton onClick={() => setEditingSection(null)}>
                <XMarkIcon className="w-5 h-5" />
              </CloseButton>
            </ModalHeader>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleUpdateSection({
                name: formData.get('name'),
                columns: parseInt(formData.get('columns'), 10),
              });
            }}>
              <FormGroup>
                <Label>Section Name</Label>
                <Input
                  name="name"
                  defaultValue={sectionDisplayName(editingSection)}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Columns</Label>
                <Select name="columns" defaultValue={sectionColumns(editingSection)}>
                  <option value={1}>1 Column</option>
                  <option value={2}>2 Columns</option>
                  <option value={3}>3 Columns</option>
                </Select>
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" secondary onClick={() => setEditingSection(null)}>
                  Cancel
                </Button>
                <Button type="submit" primary>
                  Save Changes
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Add Section Modal */}
      {showAddSection && (
        <ModalOverlay onClick={() => setShowAddSection(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add New Section</ModalTitle>
              <CloseButton onClick={() => setShowAddSection(false)}>
                <XMarkIcon className="w-5 h-5" />
              </CloseButton>
            </ModalHeader>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleAddSection(sectionApiPayload({
                name: formData.get('name'),
                columns: parseInt(formData.get('columns'), 10),
              }));
            }}>
              <FormGroup>
                <Label>Section Name</Label>
                <Input
                  name="name"
                  placeholder="e.g., Electrical, Navigation"
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Columns</Label>
                <Select name="columns" defaultValue={2}>
                  <option value={1}>1 Column</option>
                  <option value={2}>2 Columns</option>
                  <option value={3}>3 Columns</option>
                </Select>
              </FormGroup>
              
              <ButtonGroup>
                <Button type="button" secondary onClick={() => setShowAddSection(false)}>
                  Cancel
                </Button>
                <Button type="submit" primary>
                  Add Section
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Widget Editor Modal */}
      {editingWidget && (
        <WidgetEditor
          widget={editingWidget}
          sections={sections}
          onSave={handleSaveWidget}
          onClose={() => setEditingWidget(null)}
          vesselId={dashboard?.vessel_id}
          defaultSectionId={editingWidget.sectionId}
          dashboardId={id}
        />
      )}

      <BilgeMonitorSettingsModal
        open={showBilgeSettings}
        onClose={() => setShowBilgeSettings(false)}
        dashboardId={id}
        vesselId={dashboard?.vessel_id}
        initialConfig={parseTheme(dashboard).bilgeMonitor}
        sections={sections}
        onSaved={handleSaveBilgeTheme}
      />
    </BuilderContainer>
  );
};

export default EditDashboards;